/**
 * Logica condivisa per il calcolo del tipo di validazione sugli attributi.
 * Mappa i pattern semantici del nome campo → ID in SKE_TipiValidazioni (FK).
 * 
 * La tabella SKE_TipiValidazioni contiene i pattern Zod/C# con placeholder :max.
 * La vista SKE_AttributiVista fa il REPLACE(:max, ATT_Lunghezza) e produce la stringa finale.
 * 
 * Usato da:
 * - AttributiPage.jsx (suggerimento dinamico quando l'utente modifica ATT_Nome)
 */

/**
 * Costanti ID della tabella SKE_TipiValidazioni.
 * Corrispondono alle 14 righe nel database.
 */
export const TIV = {
    MAXLEN:    1,  // .max(:max)
    MINLEN:    2,  // .min(1)
    URLMAX:    3,  // .max(:max).url()        — retrocompat, URL con lunghezza
    EMAILMAX:  4,  // .max(:max).email()      — retrocompat, email con lunghezza
    DATA:      5,  // .regex(timestamp)
    URL:       6,  // .url()                  — URL pura
    EMAIL:     7,  // .email()                — email pura
    ORA:       8,  // .regex(HH:mm:ss)
    CODFIS:    9,  // .regex(CF).length(16)
    PIVA:     10,  // .regex(PIVA).length(11)
    CAP:      11,  // .regex(CAP).length(5)
    PASSWORD: 12,  // .min(8)
    IBAN:     13,  // .regex(IBAN)
    NONNEG:   14,  // .nonnegative()
};

/**
 * Determina il tipo di validazione (ID TipiValidazioni) per un attributo,
 * basandosi sul pattern semantico del nome campo, tipo campo e lunghezza.
 * 
 * L'ordine di valutazione è critico: le validazioni semantiche specifiche
 * (CF, PIVA, ecc.) vengono valutate PRIMA del catch-all TIVMAXLEN.
 * Replica fedelmente la logica dello script SQL (FASE 3).
 * 
 * @param {string} fieldName - Nome dell'attributo (ATT_Nome)
 * @param {string|null} typeScriptType - Tipo TypeScript ('string', 'number', 'boolean', etc.)
 * @param {string|number|null} fieldLength - Lunghezza del campo (ATT_Lunghezza)
 * @param {number|null} tipoId - ID del tipo campo (ATT_TipiCampi)
 * @param {number|null} superOggetti - ID super oggetto (ATT_Super_Oggetti), 0 = nessuno
 * @returns {number|null} - ID TipiValidazioni (1-14) o null se nessuna validazione
 */
export function computeValidationType(fieldName, typeScriptType, fieldLength, tipoId, superOggetti) {
    if (!fieldName) return null;

    const nome = fieldName.toLowerCase();
    const hasLength = fieldLength && !isNaN(parseInt(fieldLength, 10)) && parseInt(fieldLength, 10) > 0;

    // ─── Priorità 1: CODICE FISCALE ───
    if (typeScriptType === 'string' && (nome.includes('codicefiscale') || /(?:^|[^a-z])cf(?:$|[^a-z])/.test(nome))) {
        return TIV.CODFIS;
    }

    // ─── Priorità 2: PARTITA IVA ───
    if (typeScriptType === 'string' && (nome.includes('partitaiva') || nome.includes('piva'))) {
        return TIV.PIVA;
    }

    // ─── Priorità 3: CAP (parola intera, no "capacita", "capitolo") ───
    if (typeScriptType === 'string' && /(?:^|[^a-z])cap(?:$|[^a-z])/.test(nome)) {
        return TIV.CAP;
    }

    // ─── Priorità 4: IBAN ───
    if (typeScriptType === 'string' && nome.includes('iban')) {
        return TIV.IBAN;
    }

    // ─── Priorità 5: PASSWORD ───
    if (typeScriptType === 'string' && nome.includes('password')) {
        return TIV.PASSWORD;
    }

    // ─── Priorità 6-7: EMAIL / MAIL / PEC (parola intera per PEC) ───
    if (typeScriptType === 'string' && (
        nome.includes('email') || nome.includes('mail') ||
        /(?:^|[^a-z])pec(?:$|[^a-z])/.test(nome)
    )) {
        return hasLength ? TIV.EMAILMAX : TIV.EMAIL;
    }

    // ─── Priorità 8-9: URL / SITO WEB ───
    if (typeScriptType === 'string' && (
        nome.includes('url') || nome.includes('sitoweb') || nome.includes('website') ||
        nome.includes('homepage') || nome.includes('link')
    )) {
        return hasLength ? TIV.URLMAX : TIV.URL;
    }

    // ─── Priorità 10: DATA / TIMESTAMP (tipo campo 3 o 4) ───
    if (tipoId === 3 || tipoId === 4) {
        return TIV.DATA;
    }

    // ─── Priorità 11: ORA (tipo campo 6) ───
    if (tipoId === 6) {
        return TIV.ORA;
    }

    // ─── Priorità 12: ID non-negativo (Id[A-Z]..., intero, senza super-oggetto) ───
    if (/^[Ii]d[A-Z]/.test(fieldName) && (tipoId === 2 || tipoId === 13) && (!superOggetti || superOggetti === 0)) {
        return TIV.NONNEG;
    }

    // ─── Priorità 13: Catch-all — stringa con lunghezza → MAXLEN ───
    if (typeScriptType === 'string' && hasLength) {
        return TIV.MAXLEN;
    }

    return null;
}
